import { useLocation, useNavigate } from 'react-router-dom';
import { inferRouteContext } from './pathInference';

/**
 * Sticky bottom-bar — присутствует на всех экранах кроме Главной (P-023, CLAUDE.md §16.4).
 *
 * variant='default' (обычные экраны):
 * - 3 кнопки: Назад / На главную / Что улучшить.
 * - «Что улучшить» **всегда** рендерится. Default-flow:
 *   `navigate('/feedback', { state: { blockId, screenId } })` с context'ом
 *   из `useLocation().pathname` через `inferRouteContext`. Опциональный
 *   `onFeedback` prop сохранён как escape hatch для редких случаев когда
 *   нужна кастомная логика (например, FeedbackFormView передаёт `() => {}`
 *   no-op чтобы не уйти в loop /feedback → /feedback).
 *
 * variant='home' (Экран 2.0 Главная):
 * - 2 кнопки: Выход / Что улучшить.
 * - Кнопок «Назад» и «На главную» нет (мы и так на Главной).
 *
 * «Назад» (P-036): вызывает `navigate(-1)` если есть история, иначе `navigate('/')` —
 * иначе кнопка молча не работает при прямом входе на URL.
 *
 * **Phase 3 default-navigate flow (ADR-0018 §Q9):** все consumer'ы под управлением
 * Phase 5 cleanup wave убрали legacy alert-stub'ы — 26 файлов очищено,
 * `apps/web/src/features/configurator/*` сохранены под file guard'ом
 * (Aibek active Block 6 v2 work, отдельный rebase point).
 *
 * References:
 *   - ADR-0018 §Q5 (blockId/screenId source via useLocation().state)
 *   - ADR-0018 §Q9 (BottomBar consumer migration — Phase 5 cleanup completed)
 *   - P-031 (anti-parsing — state via React Router, не URL params)
 *   - B-085 (future refactor: route.handle metadata вместо pathInference switch)
 */
export interface BottomBarProps {
  variant?: 'default' | 'home';
  onFeedback?: () => void;
  onLogout?: () => void;
  /**
   * PR-B1 (ADR-0034) — optional hook, вызывается синхронно ПЕРЕД `navigate('/')`
   * при клике «На главную». Договор (Блок 13) использует его для очистки
   * реквизитов клиента на session-end. Optional: `undefined` → текущее
   * поведение, остальные consumer'ы не затронуты.
   */
  onBeforeNavigateHome?: () => void;
  /**
   * F2 (ADR-0080) — явный адрес возврата для кнопки «Назад» (вместо `navigate(-1)`).
   * Каталог Партнёров передаёт сюда валидированный (анти-open-redirect, внутренний
   * относительный путь) `returnTo`, чтобы «Назад» вёл в калькулятор-анкету, откуда
   * пришёл пользователь (R3: persistent intent). Optional: `undefined` → текущее
   * history-поведение (`navigate(-1)` / `navigate('/')`), остальные consumer'ы не
   * затронуты. Caller обязан санировать значение (см. `features/partners/returnTo.ts`).
   */
  backTo?: string;
}

export function BottomBar({
  variant = 'default',
  onFeedback,
  onLogout,
  onBeforeNavigateHome,
  backTo,
}: BottomBarProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    // F2: явный returnTo имеет приоритет над историей (caller уже санировал путь).
    if (backTo) {
      navigate(backTo);
      return;
    }
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  // Backward-compat (ADR-0018 §Q9): legacy consumers pass alert-stub `onFeedback`.
  // Default — навигация на /feedback c routeContext в location.state.
  const handleFeedback =
    onFeedback ??
    (() => {
      const { blockId, screenId } = inferRouteContext(location.pathname);
      navigate('/feedback', { state: { blockId, screenId } });
    });

  const buttons: Array<{ key: string; label: string; onClick: () => void }> = [];

  if (variant === 'home') {
    if (onLogout) {
      buttons.push({ key: 'logout', label: 'Выход', onClick: onLogout });
    }
  } else {
    buttons.push({ key: 'back', label: 'Назад', onClick: handleBack });
    buttons.push({
      key: 'home',
      label: 'На главную',
      // PR-B1 (ADR-0034): даём parent'у шанс на session-end cleanup до навигации.
      onClick: () => {
        onBeforeNavigateHome?.();
        navigate('/');
      },
    });
  }

  // «Что улучшить» — всегда (handleFeedback гарантированно функция: либо
  // legacy onFeedback prop, либо default navigate flow).
  buttons.push({ key: 'feedback', label: 'Что улучшить', onClick: handleFeedback });

  return (
    <nav
      className="sticky bottom-0 flex items-stretch bg-header border-t border-header-border"
      style={{ height: '53px' }}
      aria-label="Нижняя панель навигации"
    >
      {buttons.map((btn, i) => (
        <button
          key={btn.key}
          type="button"
          onClick={btn.onClick}
          aria-label={btn.label}
          className={
            'flex-1 flex items-center justify-center ' +
            'bg-transparent text-ink text-[13px] font-medium tracking-wide ' +
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80 ' +
            (i < buttons.length - 1 ? 'border-r border-header-border' : '')
          }
        >
          <span>{btn.label}</span>
        </button>
      ))}
    </nav>
  );
}
